import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/styles/richTextEditor.css";
import App from "@/App";
import { consumeAuthHandoffFromUrl } from "@/utils/crossHostAuth";

// Radix/shadcn popovers + our dialogs can emit a benign
// "ResizeObserver loop completed with undelivered notifications" during layout.
// In dev, CRA's error overlay treats it as a runtime error and covers the page
// with an iframe that swallows every click (it blocked the whole Backup &
// Restore screen). Nothing is actually broken, so stop it bubbling.
window.addEventListener("error", (e) => {
  if (e.message && e.message.includes("ResizeObserver loop")) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

// iter312 — Cross-host auth handoff: when a tenant admin is bounced
// from `my.vyomos.io` (platform host) to their tenant's host, the
// outgoing redirect encodes the JWT into the URL fragment. Pick it
// up BEFORE React mounts so AuthContext sees a logged-in session on
// first paint instead of redirecting to the login page.
consumeAuthHandoffFromUrl();

// iter311 — Canonical host redirect: www → apex.
// Many tenants register only the apex (`example.com`) with their
// Cloudflare-for-SaaS hostname, so the `www.example.com` variant
// has no backend routing and every `/api/...` call fails with
// ERR_CONNECTION_CLOSED ("Cloudflare could not parse" toasts,
// missing tournaments, stuck-pending payments). Bouncing the user
// to the apex BEFORE React mounts fixes all of those in one shot.
// We skip the redirect on localhost / preview hosts and on bare
// `www.` (root-only) so we don't loop or trap dev work.
//
// iter318 wave 20 (2026-02) — ALSO skip our own marketing hosts:
// on VyomOS, the canonical direction is REVERSED — Cloudflare
// 308-redirects `vyomos.io` → `www.vyomos.io`. Stripping the
// `www.` here would land back on the apex, which Cloudflare then
// bounces back to `www.`, producing an infinite `www ⇄ apex`
// reload loop on the marketing homepage. The list of platform
// hosts must stay in sync with `_DEFAULT_HOSTS` in
// `backend/tenancy/middleware.py`.
const PLATFORM_HOSTS = new Set([
  'www.vyomos.io',
  'vyomos.io',
  'my.vyomos.io',
]);
(() => {
  try {
    const h = window.location.hostname.toLowerCase();
    if (
      h.startsWith('www.') &&
      h !== 'www.' &&
      !h.endsWith('.localhost') &&
      h !== 'localhost' &&
      !PLATFORM_HOSTS.has(h)
    ) {
      const target = window.location.protocol + '//' + h.slice(4) +
        window.location.pathname + window.location.search + window.location.hash;
      window.location.replace(target);
      return; // short-circuit; the redirect will reload the page
    }
  } catch {
    // never block app boot on the canonical-host check
  }
})();

// Suppress ResizeObserver loop error (common with Radix UI/Shadcn components)
// This is a benign warning that doesn't affect functionality
const resizeObserverErr = window.onerror;
window.onerror = (message, ...args) => {
  if (typeof message === 'string' && message.includes('ResizeObserver loop')) {
    return true;
  }
  if (resizeObserverErr) {
    return resizeObserverErr(message, ...args);
  }
};

// Handle error events
window.addEventListener('error', (e) => {
  if (e.message && e.message.includes('ResizeObserver loop')) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return true;
  }
});

// Handle unhandled promise rejections for this error
window.addEventListener('unhandledrejection', (e) => {
  if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver loop')) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return true;
  }
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
